import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Search, Heart, ShoppingBag, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop" },
    { to: "/categories", label: "Categories" },
    { to: "/about", label: "About ELVOSH" },
    { to: "/contact", label: "Contact Us" },
    { to: "/faq", label: "FAQ" },
];

export default function Header() {
    const { count } = useCart();
    const { count: wishCount } = useWishlist();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const submitSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setOpen(false);
            setQ("");
        }
    };

    return (
        <header className="sticky top-0 z-50 bg-white/75 backdrop-blur-xl border-b border-[#E8DFD5]/60">
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
                <div className="flex h-16 md:h-20 items-center justify-between gap-4">
                    <div className="flex items-center gap-2 md:hidden">
                        <Sheet open={open} onOpenChange={setOpen}>
                            <SheetTrigger asChild>
                                <button data-testid="mobile-menu-btn" aria-label="Menu" className="p-1">
                                    <Menu className="h-6 w-6" strokeWidth={1.5} />
                                </button>
                            </SheetTrigger>
                            <SheetContent side="left" className="bg-cream w-[85vw] max-w-sm border-r border-[#E8DFD5]">
                                <div className="font-serif text-2xl tracking-[0.25em] mb-8 mt-2">ELVOSH</div>
                                <form onSubmit={submitSearch} className="mb-6 flex items-center gap-2 border-b border-[#E8DFD5] pb-2">
                                    <Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                                    <input
                                        data-testid="mobile-search-input"
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        placeholder="Search products…"
                                        className="w-full bg-transparent text-sm outline-none"
                                    />
                                </form>
                                <nav className="flex flex-col gap-1">
                                    {NAV.map((n) => (
                                        <Link
                                            key={n.to}
                                            to={n.to}
                                            data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                                            onClick={() => setOpen(false)}
                                            className="py-3 text-base border-b border-[#E8DFD5]/60 uppercase tracking-wider"
                                        >
                                            {n.label}
                                        </Link>
                                    ))}
                                    <Link to="/wishlist" onClick={() => setOpen(false)} className="py-3 text-base border-b border-[#E8DFD5]/60 uppercase tracking-wider">Wishlist</Link>
                                    <Link to={user ? "/account" : "/login"} onClick={() => setOpen(false)} className="py-3 text-base uppercase tracking-wider">{user ? "My Account" : "Login / Account"}</Link>
                                </nav>
                            </SheetContent>
                        </Sheet>
                    </div>

                    <Link to="/" data-testid="logo-home" className="font-serif text-2xl md:text-[28px] tracking-[0.28em] leading-none">
                        ELVOSH
                    </Link>

                    <nav className="hidden md:flex items-center gap-8 text-[13px] uppercase tracking-[0.15em]">
                        {NAV.map((n) => (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                                className={`link-underline transition-colors hover:text-gold ${location.pathname === n.to ? "text-gold" : ""}`}
                            >
                                {n.label}
                            </Link>
                        ))}
                    </nav>

                    <div className="flex items-center gap-4 md:gap-5">
                        <button data-testid="search-toggle" aria-label="Search" onClick={() => setSearchOpen((s) => !s)} className="hidden md:block hover:text-gold transition-colors">
                            <Search className="h-5 w-5" strokeWidth={1.5} />
                        </button>
                        <Link to="/wishlist" data-testid="wishlist-link" aria-label="Wishlist" className="relative hover:text-gold transition-colors">
                            <Heart className="h-5 w-5" strokeWidth={1.5} />
                            {wishCount > 0 && <span className="absolute -top-2 -right-2 bg-gold text-white text-[10px] h-4 min-w-4 px-1 rounded-full flex items-center justify-center">{wishCount}</span>}
                        </Link>
                        <Link to="/cart" data-testid="cart-link" aria-label="Cart" className="relative hover:text-gold transition-colors">
                            <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
                            {count > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-gold text-white text-[10px] h-4 min-w-4 px-1 rounded-full flex items-center justify-center">{count}</span>}
                        </Link>
                        <Link to={user ? "/account" : "/login"} data-testid="account-link" aria-label="Account" className="hover:text-gold transition-colors">
                            <User className="h-5 w-5" strokeWidth={1.5} />
                        </Link>
                    </div>
                </div>

                {searchOpen && (
                    <form onSubmit={submitSearch} className="hidden md:flex items-center gap-3 pb-4 -mt-1 border-b border-[#E8DFD5] fade-up">
                        <Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                        <input
                            data-testid="desktop-search-input"
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search for straighteners, stylers…"
                            className="w-full bg-transparent text-sm py-2 outline-none"
                        />
                        <button type="button" onClick={() => setSearchOpen(false)}><X className="h-4 w-4" /></button>
                    </form>
                )}
            </div>
        </header>
    );
}
